import { EmptyState, SectionCard } from '@atlas/ui-components'
import { Chip } from '@atlas/ui-primitives'
import { ArrowUpRight } from 'lucide-react'
import type { BrandMetaLink } from './brand-meta'

export interface BrandLinksCardProps {
	/** The brand's collections, from `brand.meta.links`. Empty renders the gap, not a sample. */
	links: BrandMetaLink[]
}

/**
 * Where this brand's assets live outside ATLAS.
 *
 * "Brand links", not "Veeva links": a brand's collections span Veeva, the brand DAM and its public
 * site, and naming the card after one vendor mis-describes the other rows the moment a second
 * system appears. Each row states its own `kind` instead.
 *
 * A link whose `url` is null is rendered as TEXT, not a dead anchor — the collection is known to
 * exist but its address is not recorded, and an anchor that goes nowhere reads as a broken link
 * rather than as missing data.
 *
 * Empty is a gap, not sample data: a brand with no links recorded shows that, which is the
 * page's job. This is why the card no longer carries a `samplePending` notice.
 */
export function BrandLinksCard({ links }: BrandLinksCardProps) {
	return (
		<SectionCard title="Brand links">
			{links.length === 0 ? (
				<EmptyState title="No collections are recorded for this brand yet." />
			) : (
				<ul className="space-y-2.5">
					{links.map((link) => (
						<li key={link.label} className="flex flex-wrap items-center gap-1.5">
							{link.url ? (
								<a
									href={link.url}
									target="_blank"
									// `noopener` as well as `noreferrer`: these point at third-party systems, and
									// without it the opened page can reach back through `window.opener`.
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1.5 text-sm text-ds-accent hover:underline"
								>
									<ArrowUpRight aria-hidden className="h-3.5 w-3.5" />
									{link.label}
								</a>
							) : (
								<span className="text-sm text-ds-text-muted">{link.label}</span>
							)}
							{link.kind ? (
								<Chip variant="muted" size="sm">
									{link.kind}
								</Chip>
							) : null}
						</li>
					))}
				</ul>
			)}
		</SectionCard>
	)
}
